import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MessageSquare,
  Users,
  CheckCircle2,
  AlertTriangle,
  History,
  RefreshCw,
  Search,
  MessageCircleOff,
  ChevronRight,
  ChevronDown,
  Filter,
} from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis, Cell } from 'recharts';
import {
  fetchChatbotSummary,
  fetchChatbotIntents,
  fetchRecentChatSessions,
  fetchUnansweredQuestions,
  type ChatbotSummary,
  type IntentDistribution,
  type ChatSession,
  type UnansweredQuestion,
} from '../../services/MasterAdmin/chatbotIntelligenceService';
import InsightsPageShell from '../../components/MasterAdmin/insights/InsightsPageShell';
import InsightsStatCard from '../../components/MasterAdmin/insights/InsightsStatCard';
import InsightsChartCard, { INSIGHTS_CHART_TOOLTIP } from '../../components/MasterAdmin/insights/InsightsChartCard';
import InsightsSectionHeader from '../../components/MasterAdmin/insights/InsightsSectionHeader';
import MetricInfoPopover from '../../components/MasterAdmin/ui/MetricInfoPopover';
import { opsFilterClass, opsMetricGridClass, opsPrimaryBtn, PIE_PALETTE } from '../../components/MasterAdmin/operations/operationsUi';
import { chatbotIntelligenceMetricInfo } from '../../config/masterAdminMetricDefinitions';

// Bulletproof date parser for BigQuery timestamps (Handles Strings and Objects)
const formatDate = (dateStr: any) => {
  if (!dateStr) return 'N/A';

  let raw = dateStr;
  if (typeof dateStr === 'object' && dateStr !== null) {
    raw = dateStr.value || dateStr.date || JSON.stringify(dateStr);
  }

  try {
    const date = new Date(typeof raw === 'string' ? raw.replace(' ', 'T').replace(' UTC', '') : raw);

    if (!isNaN(date.getTime())) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
    }

    const timeMatch = String(raw).match(/(\d{2}:\d{2}:\d{2})/);
    if (timeMatch) return timeMatch[1];

    return String(raw).split('.')[0] || String(raw);
  } catch (e) {
    return String(raw).substring(0, 20);
  }
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  if (status === 'answered') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-1 text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
        <CheckCircle2 size={12} />
        ANSWERED
      </span>
    );
  }
  if (status === 'fallback') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-1 text-[11px] font-bold text-amber-600 dark:text-amber-400">
        <AlertTriangle size={12} />
        FALLBACK
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-red-500/10 px-2 py-1 text-[11px] font-bold text-red-600 dark:text-red-400">
      <MessageCircleOff size={12} />
      UNANSWERED
    </span>
  );
};

const ChatbotIntelligencePage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<ChatbotSummary | null>(null);
  const [intents, setIntents] = useState<IntentDistribution[]>([]);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [unanswered, setUnanswered] = useState<UnansweredQuestion[]>([]);
  const [days, setDays] = useState(30);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'answered' | 'unanswered'>('all');
  const [intentFilter, setIntentFilter] = useState('all');
  const [expandedUsers, setExpandedUsers] = useState<string[]>([]);

  const toggleUser = (userId: string) => {
    setExpandedUsers((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]));
  };

  const filteredSessions = useMemo(
    () =>
      sessions.filter((msg) => {
        const matchesStatus = statusFilter === 'all' || msg.response_status.toLowerCase() === statusFilter;
        const matchesIntent = intentFilter === 'all' || msg.intent === intentFilter;
        const matchesSearch =
          !searchTerm ||
          msg.user_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          msg.question.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesStatus && matchesIntent && matchesSearch;
      }),
    [sessions, statusFilter, intentFilter, searchTerm]
  );

  const userGroups = useMemo(
    () =>
      filteredSessions.reduce((groups: Record<string, ChatSession[]>, msg) => {
        if (!groups[msg.user_id]) groups[msg.user_id] = [];
        groups[msg.user_id].push(msg);
        return groups;
      }, {}),
    [filteredSessions]
  );

  const availableIntents = useMemo(() => Array.from(new Set(sessions.map((s) => s.intent))).filter(Boolean), [sessions]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [sumData, intentData, sessionData, unansweredData] = await Promise.all([
        fetchChatbotSummary(days),
        fetchChatbotIntents(days),
        fetchRecentChatSessions(50),
        fetchUnansweredQuestions(days),
      ]);
      setSummary(sumData);
      setIntents(intentData);
      setSessions(sessionData);
      setUnanswered(unansweredData);
    } catch (error) {
      console.error('Failed to load chatbot analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  return (
    <InsightsPageShell
      title="Chatbot Intelligence"
      description="Monitor bot performance, hallucinations, and knowledge gaps."
      info={chatbotIntelligenceMetricInfo.page}
      actions={
        <>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className={opsFilterClass}
          >
            <option value={10}>Last 10 Days</option>
            <option value={20}>Last 20 Days</option>
            <option value={30}>Last 30 Days</option>
          </select>
          <button type="button" onClick={() => void loadData()} disabled={loading} className={opsPrimaryBtn}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </>
      }
    >
      <div className={opsMetricGridClass}>
        <InsightsStatCard
          label="Total Messages"
          value={(summary?.total_messages || 0).toLocaleString()}
          icon={MessageSquare}
          loading={loading}
          info={chatbotIntelligenceMetricInfo.totalMessages}
        />
        <InsightsStatCard
          label="Unique Users"
          value={(summary?.unique_users || 0).toLocaleString()}
          icon={Users}
          loading={loading}
          info={chatbotIntelligenceMetricInfo.uniqueUsers}
        />
        <InsightsStatCard
          label="Success Rate"
          value={`${Math.round(summary?.success_rate || 0)}%`}
          icon={CheckCircle2}
          accent="text-emerald-600 dark:text-emerald-400"
          loading={loading}
          info={chatbotIntelligenceMetricInfo.successRate}
        />
        <InsightsStatCard
          label="Fallbacks"
          value={(summary?.unanswered_count || 0).toLocaleString()}
          icon={AlertTriangle}
          accent="text-red-500"
          loading={loading}
          info={chatbotIntelligenceMetricInfo.fallbacks}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <InsightsChartCard title="Intent Distribution" info={chatbotIntelligenceMetricInfo.intentDistribution} heightClass="h-72">
          {loading ? (
            <div className="flex h-full items-center justify-center">
              <RefreshCw className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          ) : intents.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-gray-500">No intent data</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={intents.slice(0, 8)} layout="vertical" margin={{ top: 4, right: 16, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-20" horizontal={false} />
                <XAxis type="number" hide />
                <YAxis
                  dataKey="intent"
                  type="category"
                  width={130}
                  tick={{ fontSize: 11, fill: '#94a3b8' }}
                  axisLine={false}
                  tickLine={false}
                />
                <RechartsTooltip contentStyle={INSIGHTS_CHART_TOOLTIP} cursor={{ fill: 'rgba(148, 163, 184, 0.1)' }} />
                <Bar dataKey="count" radius={[0, 6, 6, 0]} maxBarSize={22}>
                  {intents.slice(0, 8).map((_, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_PALETTE[index % PIE_PALETTE.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </InsightsChartCard>

        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-5">
          <InsightsSectionHeader title="Unanswered Questions" info={chatbotIntelligenceMetricInfo.unansweredQuestions} />
          <div className="max-h-72 overflow-y-auto pr-1">
            {loading ? (
              <div className="flex h-40 items-center justify-center">
                <RefreshCw className="h-6 w-6 animate-spin text-primary-600" />
              </div>
            ) : unanswered.length > 0 ? (
              <div className="space-y-3">
                {unanswered.map((q, i) => (
                  <div key={i} className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
                    <p className="mb-1 text-sm font-medium text-gray-800 dark:text-gray-200">&quot;{q.question}&quot;</p>
                    <div className="flex items-center justify-between">
                      <span className="rounded-full bg-red-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-600 dark:text-red-400">
                        Fallback
                      </span>
                      <span className="text-[10px] text-gray-500 dark:text-gray-500">{formatDate(q.timestamp)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-2 py-12 text-gray-400 dark:text-gray-500">
                <CheckCircle2 size={32} />
                <p>No unanswered questions found!</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-col gap-4 border-b border-gray-100 p-4 dark:border-gray-700 md:flex-row md:items-center md:justify-between sm:p-5">
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-primary-600 dark:text-primary-400" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Live Conversation Audit</h3>
            {chatbotIntelligenceMetricInfo.liveConversationAudit && (
              <MetricInfoPopover title="Live Conversation Audit" info={chatbotIntelligenceMetricInfo.liveConversationAudit} />
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex rounded-lg border border-gray-200 bg-gray-100 p-1 dark:border-gray-700 dark:bg-gray-900/40">
              <button
                type="button"
                onClick={() => setStatusFilter('all')}
                className={`rounded-md px-3 py-1 text-xs font-bold transition-all ${
                  statusFilter === 'all'
                    ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-800 dark:text-white'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                All
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter('answered')}
                className={`rounded-md px-3 py-1 text-xs font-bold transition-all ${
                  statusFilter === 'answered'
                    ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-800 dark:text-white'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                Answered
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter('unanswered')}
                className={`rounded-md px-3 py-1 text-xs font-bold transition-all ${
                  statusFilter === 'unanswered'
                    ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-800 dark:text-white'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                Unanswered
              </button>
            </div>

            <div className="relative">
              <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
              <select
                value={intentFilter}
                onChange={(e) => setIntentFilter(e.target.value)}
                className={`${opsFilterClass} appearance-none pl-9`}
              >
                <option value="all">All Intents</option>
                {availableIntents.map((intent) => (
                  <option key={intent} value={intent}>
                    {intent.replace('_', ' ').toUpperCase()}
                  </option>
                ))}
              </select>
            </div>

            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Search sessions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`${opsFilterClass} w-48 pl-9`}
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-400">
                <th className="w-10 px-4 py-3"></th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3 text-center">Messages</th>
                <th className="px-4 py-3">Last Activity</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-500">
                    <RefreshCw className="mx-auto mb-2 h-6 w-6 animate-spin text-primary-600" />
                    Loading conversations…
                  </td>
                </tr>
              ) : Object.keys(userGroups).length > 0 ? (
                Object.keys(userGroups).map((userId) => {
                  const userMessages = userGroups[userId];
                  const lastMessage = userMessages[0];
                  const isExpanded = expandedUsers.includes(userId);

                  return (
                    <React.Fragment key={userId}>
                      <tr
                        onClick={() => toggleUser(userId)}
                        className={`group cursor-pointer transition-all hover:bg-gray-50 dark:hover:bg-gray-900/40 ${
                          isExpanded ? 'bg-gray-50 dark:bg-gray-900/60' : ''
                        }`}
                      >
                        <td className="px-4 py-3">
                          {isExpanded ? (
                            <ChevronDown size={18} className="text-primary-600 dark:text-primary-400" />
                          ) : (
                            <ChevronRight
                              size={18}
                              className="text-gray-400 transition-colors group-hover:text-primary-500 dark:text-gray-600 dark:group-hover:text-primary-400"
                            />
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-gray-100 text-xs font-bold text-primary-600 dark:border-gray-700 dark:bg-gray-800 dark:text-primary-400">
                              {userId.substring(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <p className="text-sm font-bold text-gray-900 dark:text-gray-200">{userId}</p>
                              <p className="font-mono text-[10px] text-gray-500 opacity-60 dark:text-gray-500">
                                ID: {userId.split('-')[0]}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-bold text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                            {userMessages.length}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-500">{formatDate(lastMessage.timestamp)}</td>
                        <td className="px-4 py-3 text-right">
                          <button type="button" className="text-xs font-bold text-primary-600 transition-colors hover:text-primary-700 dark:text-primary-400">
                            Inspect
                          </button>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr>
                          <td colSpan={5} className="bg-gray-50/60 px-4 py-4 dark:bg-gray-900/30">
                            <div className="ml-4 overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
                              <table className="w-full text-left text-xs">
                                <thead className="border-b border-gray-200 bg-gray-100/60 text-gray-500 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-500">
                                  <tr>
                                    <th className="px-4 py-3">Time</th>
                                    <th className="w-1/2 px-4 py-3">Message</th>
                                    <th className="px-4 py-3">Intent</th>
                                    <th className="px-4 py-3">Status</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                                  {userMessages.map((msg, idx) => (
                                    <tr key={idx} className="hover:bg-gray-100/40 dark:hover:bg-gray-800/30">
                                      <td className="px-4 py-3 text-gray-500 dark:text-gray-500">{formatDate(msg.timestamp)}</td>
                                      <td className="px-4 py-3 italic text-gray-700 dark:text-gray-300">&quot;{msg.question}&quot;</td>
                                      <td className="px-4 py-3">
                                        <span className="rounded bg-primary-500/10 px-2 py-0.5 text-[10px] font-medium uppercase text-primary-600 dark:text-primary-400">
                                          {msg.intent}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3">
                                        <StatusBadge status={msg.response_status} />
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-500 dark:text-gray-500">
                    No conversations found matching your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </InsightsPageShell>
  );
};

export default ChatbotIntelligencePage;
